import { useContext, useEffect, useMemo, useRef, useState } from "react"
import TextareaAutosize from "react-textarea-autosize"
import { SheetField, UICell } from "../types/UITypes"
import "./FormulaEditor.css"
import { AppContext } from "../AppContext"
import { ReactComponent as CrossIcon } from "../assets/icons/cross-icon.svg"

type FormulaEditorProps = {
  cell: UICell
  onConfirm: (value: string, cell: UICell) => void
  onClose?: () => void
  sheetFields: SheetField[]
  editingEnabled: boolean
  textEditorStyles?: object
  onInternalValueChange?: (value: string) => void
}

export const FormulaEditor = (props: FormulaEditorProps) => {
  const {
    onConfirm,
    onClose,
    cell,
    editingEnabled,
    textEditorStyles = {},
    onInternalValueChange,
  } = props
  const [inlineEditorValue, setInlineEditorValue] = useState("")
  const [formulaHint, setFormulaHint] = useState({
    operator: "",
    showFullDescription: false,
  })
  const { setEditedCell } = useContext(AppContext)

  const inlineEditorInputRef = useRef<HTMLTextAreaElement>(null)
  const confirmChangeTimeoutRef = useRef<number>()

  const handleInlineEditorValueChange = () => {
    if (!editingEnabled) {
      setEditedCell(cell)
    }

    if (!inlineEditorInputRef.current) {
      return
    }

    const target = inlineEditorInputRef.current
    const { value } = target

    setInlineEditorValue(value)

    if (value.match(/^=and\(.*\)$/i) || value.match(/^=or\(.*\)$/i)) {
      setFormulaHint({
        operator: "",
        showFullDescription: false,
      })
    } else if (value.match(/^=a(nd?\(?.*)?$/i)) {
      setFormulaHint({
        operator: "AND",
        showFullDescription: value.length >= 5,
      })
    } else if (value.match(/^=o(r(\(?.*)?)?$/i)) {
      setFormulaHint({
        operator: "OR",
        showFullDescription: value.length >= 4,
      })
    } else {
      setFormulaHint({
        operator: "",
        showFullDescription: false,
      })
    }
    onInternalValueChange && onInternalValueChange(value)
  }

  useEffect(() => {
    if (!cell || !!inlineEditorValue) {
      return
    }

    const { name } = cell

    setInlineEditorValue(name || "")

    if (cell && inlineEditorInputRef.current) {
      inlineEditorInputRef.current.focus()
    }
  }, [cell])

  const [isFocused, setFocused] = useState(false)

  const formulaHintContent = useMemo(() => {
    switch (formulaHint.operator) {
      case "AND":
        return !formulaHint.showFullDescription ? (
          <div className="formula-hint-short">
            <div style={{ backgroundColor: "#e6e6e6", padding: 10 }}>
              Logical "and" operator
            </div>
            <div
              style={{
                borderTop: "1px solid grey",
                fontSize: 12,
                padding: 10,
              }}
            >
              <span
                style={{
                  padding: "1px 2px",
                  border: "1px solid grey",
                  borderRadius: 3,
                }}
              >
                Tab
              </span>{" "}
              to accept
            </div>
          </div>
        ) : (
          <div className="formula-hint-full">
            <div
              style={{
                padding: 10,
              }}
            >
              AND(
              <span
                style={{
                  fontWeight: !inlineEditorValue.includes(",")
                    ? "bold"
                    : "normal",
                }}
              >
                logical_expression1,
              </span>
              <span
                style={{
                  fontWeight: inlineEditorValue.includes(",")
                    ? "bold"
                    : "normal",
                }}
              >
                {" "}
                logical_expression2, ...)
              </span>
            </div>
          </div>
        )
      case "OR":
        return !formulaHint.showFullDescription ? (
          <div className="formula-hint-short">Logical "or" operator</div>
        ) : (
          <div className="formula-hint-full">
            OR(
            <span
              style={{
                fontWeight: !inlineEditorValue.includes(",")
                  ? "bold"
                  : "normal",
              }}
            >
              logical_expression1,
            </span>
            <span
              style={{
                fontWeight: inlineEditorValue.includes(",") ? "bold" : "normal",
              }}
            >
              {" "}
              logical_expression2, ...)
            </span>
          </div>
        )
      default:
        return null
    }
  }, [formulaHint.operator, formulaHint.showFullDescription, inlineEditorValue])

  return (
    <div
      className="formula-editor"
      onFocus={() => {
        setFocused(true)
        confirmChangeTimeoutRef.current &&
          window.clearTimeout(confirmChangeTimeoutRef.current)
      }}
      onBlur={() => {
        if (cell.name !== inlineEditorValue) {
          onConfirm(inlineEditorValue, cell)
        }

        if (isFocused) {
          onClose && onClose()
        }
      }}
      onKeyDown={(e) => {
        if (
          e.key === "Tab" &&
          formulaHint.operator &&
          !formulaHint.showFullDescription
        ) {
          e.preventDefault()
          setFormulaHint((prev) => ({
            ...prev,
            showFullDescription: true,
          }))
          setInlineEditorValue(`=${formulaHint.operator}(`)
        }

        if (e.key === "Escape") {
          e.preventDefault()
          setFormulaHint((prev) => ({
            operator: "",
            showFullDescription: false,
          }))
        }
      }}
    >
      <TextareaAutosize
        id="test"
        className="text-field cell-input"
        value={inlineEditorValue}
        onChange={handleInlineEditorValueChange}
        ref={inlineEditorInputRef}
        style={{
          caretColor: editingEnabled ? "#000" : "transparent",
          ...textEditorStyles,
        }}
      />

      {formulaHintContent && (
        <div
          className="formula-hint"
          style={{ width: formulaHint.showFullDescription ? 385 : 200 }}
        >
          <CrossIcon
            style={{ position: "absolute", top: 4, right: 4 }}
            onClick={() =>
              setFormulaHint({
                operator: "",
                showFullDescription: false,
              })
            }
          />
          {formulaHintContent}
        </div>
      )}
    </div>
  )
}
